import { useState, useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { Checkbox } from "@ascleo/ui/components/ui/checkbox";
import type { EebConsentPresentation } from "@ascleo/frontdesk-contracts/eeb/kiosk";
import { Schema as S } from "effect";
import { useTranslation } from "react-i18next";
import { Building2, Check, FileCheck, X } from "lucide-react";
import { useCheckInSend, useCheckInContext } from "../context";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { useAudio } from "../../../../../lib/use-audio";
import { canRenderKimAddressQrCode, KimAddressQrCode } from "./KimAddressQrCode";
import { useKimAddress } from "../../../../../lib/use-kim-address";
import { FEATURE_FLAGS } from "../../../../../platform/defaults";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { KioskStep, KioskStepColumns } from "../../../../../components/kiosk-step";
import { KioskStatePanel } from "../../../../../components/kiosk-state-panel";
import {
  clearClientSubmissionId,
  getOrCreateClientSubmissionId,
} from "../../../../../lib/client-submission-id";

const EEB_SUBMISSION_STORAGE_KEY = "frontdesk-eeb-client-submission-id";
const EebExceptionConfirmation = S.Struct({
  exceptionConfirmed: S.Literal(true),
});

function getOrCreateSubmissionId(): string {
  return getOrCreateClientSubmissionId(EEB_SUBMISSION_STORAGE_KEY);
}

function clearSubmissionId(): void {
  clearClientSubmissionId(EEB_SUBMISSION_STORAGE_KEY);
}

/**
 * eEB consent step: captures explicit patient consent before sending an eEB
 * request (GDPR + KBV requirement — a real gate, not a prototype shortcut).
 * Records durable consent through the dedicated signed route, then proceeds.
 *
 * A one-line plain summary sits above the legal text (KC-05); the whole
 * declaration box is the toggle, with a clearly visible checkbox and a hint
 * while it is unticked (KC-37). Consent and decline are the footer actions;
 * without consent data the consent action stays disabled and the panel says
 * why (KC-42). The QR alternative renders only when a code can be shown
 * (KC-38).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <EebConsentStep />
 * ```
 */
export function EebConsentStep() {
  const { t } = useTranslation();
  const send = useCheckInSend();
  const context = useCheckInContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentError, setConsentError] = useState<string>();
  const [presentation, setPresentation] = useState<EebConsentPresentation>();
  const [clientSubmissionId] = useState(getOrCreateSubmissionId);

  // Play audio on mount
  useAudio("checkIn-eebConsent");

  // Auto-skip eEB consent when feature flag is disabled
  useEffect(
    function runEebConsentStepEffect() {
      if (!FEATURE_FLAGS.eeb) {
        send({ type: "CONSENT_DECLINED" });
      }
    },
    [send],
  );

  // The QR route needs only the practice KIM address, which the device already
  // knows; it must not depend on the consent presentation loading.
  const { kimInfo } = useKimAddress();
  const qrKimAddress =
    presentation?.practiceKimAddress ?? kimInfo?.kimAddress ?? undefined;
  const qrPracticeName =
    presentation?.practiceName ?? kimInfo?.practiceName ?? "";

  useEffect(
    function loadServerAuthorizedEebPresentation() {
      let active = true;
      if (!context.patientId) {
        setConsentError(t("self-service.check-in.eeb-consent.not-available"));
        return;
      }
      void docCirrusRpcClient.frontdesk
        .getEebConsentPresentation({ patientId: context.patientId })
        .then((loaded) => {
          if (active) setPresentation(loaded);
          return undefined;
        })
        .catch((error) => {
          frontdeskDiagnosticLogger.error(
            "Failed to load eEB consent presentation",
            { error },
          );
          if (active) {
            setConsentError(
              t("self-service.check-in.eeb-consent.load-failed"),
            );
          }
        });
      return function stopEebPresentationUpdate() {
        active = false;
      };
    },
    [context.patientId, t],
  );


  const form = useForm({
    defaultValues: {
      exceptionConfirmed: false,
    },
    validators: {
      onChange: S.toStandardSchemaV1(EebExceptionConfirmation),
    },
    onSubmit: async ({ value }) => {
      if (
        value.exceptionConfirmed !== true ||
        !context.patientId ||
        !presentation
      ) {
        setConsentError(t("self-service.check-in.eeb-consent.not-available"));
        return;
      }

      setIsSubmitting(true);
      setConsentError(undefined);
      try {
        const acceptance = await docCirrusRpcClient.frontdesk.recordEebConsent({
          patientId: context.patientId,
          clientSubmissionId,
          presentedConfigVersion: presentation.presentedConfigVersion,
          presentedConfiguredMode: presentation.presentedConfiguredMode,
          consentTextVersion: presentation.consentTextVersion,
          consentTextHash: presentation.consentTextHash,
          exceptionDeclarationVersion: presentation.exceptionDeclarationVersion,
          exceptionDeclarationHash: presentation.exceptionDeclarationHash,
          exceptionConfirmed: true,
        });
        clearSubmissionId();
        send({ type: "CONSENT_GRANTED", acceptance });
      } catch (error) {
        frontdeskDiagnosticLogger.error("Failed to record eEB consent", {
          error,
          hasPatientId: Boolean(context.patientId),
          hasConsentPresentation: Boolean(presentation),
        });
        setConsentError(t("self-service.check-in.eeb-consent.submit-failed"));
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleDecline = () => {
    clearSubmissionId();
    send({ type: "CONSENT_DECLINED" });
  };

  const exceptionConfirmed = useStore(
    form.store,
    (state) => state.values.exceptionConfirmed,
  );
  const canSubmit = useStore(form.store, (state) => state.canSubmit);

  const consentForm = (
    <form
      data-testid="frontdesk-eeb-consent-form"
      className="flex min-w-0 flex-col gap-5"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void form.handleSubmit();
      }}
    >
      {presentation ? (
        <div className="flex items-center gap-4 rounded-2xl border bg-card p-5">
          <Building2 aria-hidden className="size-7 shrink-0 text-primary" />
          <div className="min-w-0">
            <div className="text-base text-foreground">
              {t("self-service.check-in.eeb-consent.insurance")}
            </div>
            <div className="text-xl font-semibold break-words">
              {presentation.insurerName}
            </div>
          </div>
        </div>
      ) : null}

      {presentation ? (
        <section className="rounded-2xl border bg-card p-5">
          <h2 className="m-0 flex items-start gap-3 text-lg leading-snug font-semibold">
            <FileCheck aria-hidden className="mt-0.5 size-6 shrink-0 text-primary" />
            {t("self-service.check-in.eeb-consent.consent-title")}
          </h2>
          <p className="mt-3 mb-0 text-base leading-relaxed text-foreground">
            {presentation.consentText}
          </p>
        </section>
      ) : null}

      {presentation && (
        <form.Field name="exceptionConfirmed">
          {(field) => (
            <label
              htmlFor="frontdesk-eeb-exception-confirmed"
              className="flex cursor-pointer items-start gap-4 rounded-2xl border-2 border-primary bg-card p-5 has-[[data-checked]]:bg-primary-soft has-[[data-state=checked]]:bg-primary-soft"
            >
              <Checkbox
                id="frontdesk-eeb-exception-confirmed"
                data-testid="frontdesk-eeb-exception-confirmed-checkbox"
                className="mt-0.5 size-8 shrink-0 border-2 border-foreground bg-background"
                checked={field.state.value}
                onCheckedChange={(checked) =>
                  field.handleChange(checked === true)
                }
                aria-labelledby="frontdesk-eeb-exception-label"
                aria-describedby="frontdesk-eeb-exception-hint"
              />
              <span
                id="frontdesk-eeb-exception-label"
                className="text-lg leading-relaxed text-foreground"
              >
                {presentation.exceptionDeclarationText}
              </span>
            </label>
          )}
        </form.Field>
      )}

      {presentation && !exceptionConfirmed ? (
        <p
          id="frontdesk-eeb-exception-hint"
          className="m-0 text-lg font-medium text-foreground"
          aria-live="polite"
        >
          {t("self-service.check-in.eeb-consent.confirm-first")}
        </p>
      ) : null}

      {consentError && (
        <KioskStatePanel
          tone="error"
          title={consentError}
          testId="frontdesk-eeb-consent-error"
        />
      )}

      <p className="m-0 text-lg text-foreground">
        {t("self-service.check-in.eeb-consent.treatment-notice")}
      </p>
    </form>
  );

  // QR alternative. Deliberately not tied to `presentation`: when the consent
  // data fails to load, the QR route is the patient's remaining way to get
  // their proof. It renders only when a code can actually be shown, so no
  // empty card is left behind (KC-38).
  const qrAlternative = canRenderKimAddressQrCode(qrKimAddress) ? (
    <KimAddressQrCode
      kimAddress={qrKimAddress}
      practiceName={qrPracticeName}
      size={180}
      instruction={t("self-service.check-in.qr-code.alternative-app")}
    />
  ) : null;

  return (
    <KioskStep
      width={qrAlternative ? "wide" : "narrow"}
      title={t("self-service.check-in.eeb-consent.title-v2")}
      lead={t("self-service.check-in.eeb-consent.summary")}
      primaryAction={{
        label:
          presentation?.consentActionLabel ??
          t("self-service.check-in.eeb-consent.agree-button"),
        icon: Check,
        onClick: () => void form.handleSubmit(),
        disabled: !presentation || !canSubmit || exceptionConfirmed !== true,
        busy: isSubmitting,
        testId: "frontdesk-eeb-consent-agree-btn",
      }}
      secondaryAction={{
        label: t("self-service.check-in.eeb-consent.decline-button"),
        icon: X,
        onClick: handleDecline,
        disabled: isSubmitting,
        testId: "frontdesk-eeb-consent-decline-btn",
      }}
    >
      {qrAlternative ? (
        <KioskStepColumns aside={qrAlternative}>{consentForm}</KioskStepColumns>
      ) : (
        consentForm
      )}
    </KioskStep>
  );
}
